/**
 * Dark (Night Owl's own look, the default), light, or follow the device. The choice is kept per device and applied as a `dark` class on
 * <html>; index.html does the same before first paint so there's no flash. Pages clients see (their
 * hub, booking forms, referral and unsubscribe links) always stay light: they carry the business's
 * own branding.
 */
import { useSyncExternalStore } from "react";

export type ThemeChoice = "light" | "dark" | "system";

const KEY = "nightowl.theme"; // also read by the script in index.html
const CLIENT_FACING = /^\/(hub|book|u|refer)\//;
const media = window.matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<() => void>();

/** Night Owl is a night-sky brand: dark until someone picks light or their device's setting. */
export const DEFAULT_THEME: ThemeChoice = "dark";

function stored(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" || value === "system" ? value : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

let choice = stored();

function apply() {
  const dark = !CLIENT_FACING.test(window.location.pathname) && (choice === "dark" || (choice === "system" && media.matches));
  document.documentElement.classList.toggle("dark", dark);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#000000" : "#f7f4f9");
}

function changed() {
  listeners.forEach((listener) => listener());
}

export function setTheme(next: ThemeChoice) {
  choice = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Private mode: the choice lasts until the tab closes.
  }
  // Cross-fade the whole page rather than letting every color transition on its own.
  // (A background tab skips the transition; the change still applies.)
  if (document.startViewTransition) document.startViewTransition(apply).ready.catch(() => undefined);
  else apply();
  changed();
}

export function useTheme(): ThemeChoice {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => choice,
  );
}

/** Keep up with the device switching modes, and with the choice changing in another tab. */
export function initTheme() {
  media.addEventListener("change", apply);
  window.addEventListener("storage", (e) => {
    if (e.key !== KEY && e.key !== null) return;
    choice = stored();
    apply();
    changed();
  });
  apply();
}

/** Whether the page is showing dark right now (the class `apply` sets), for art drawn outside CSS. */
export function useIsDark(): boolean {
  return useSyncExternalStore(
    (listener) => {
      const observer = new MutationObserver(listener);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
      return () => observer.disconnect();
    },
    () => document.documentElement.classList.contains("dark"),
    () => true,
  );
}
